import { _isNil, _isNotBlank, _isNotEmpty, _notNil, safelyDecodeURIComponent } from '@/littledash';
import { isSubsetOfSets } from '@/utils/browser.utils';
import '../editors/MultiSelectEditor.scss';
import styles from '../editors/MultiSelectCell.module.scss';
import InVivoError from '@/model/InVivoError.ts';
import { notAborted, useAbortController } from '@/support/Hooks/fetch/useAbortController';
import Http from '@/support/http';
import { api } from '@/support/route';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import type Handsontable from 'handsontable-v16';
import { type BaseEditor, TextEditor } from 'handsontable-v16/editors';
import { registerCellType } from 'handsontable-v16/cellTypes';
import type { CellProperties } from 'handsontable-v16/settings';
import { StrictMode, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import SyncSelect from 'react-select';
import { AsyncPaginate } from 'react-select-async-paginate';
import { fromInVivoValidator, type InVivoValidator } from '@/support/hotValidator.util';
import { validationCache } from '@/utils/ValidationRequestCache';

export const loadMultiSelectRawDataV16 = (
  value: string,
  jsonEncoded: boolean,
  decode: boolean = false
): Array<string> => {
  if (jsonEncoded) {
    try {
      const jsonData = JSON.parse(_isNotBlank(value) ? value : '[]');
      if (Array.isArray(jsonData)) {
        return jsonData.reduce<Array<string>>((acc, item) => {
          switch (typeof item) {
            case 'string':
              if (_isNotBlank(item)) {
                acc.push(item.trim());
              }
              break;
            case 'boolean':
            case 'number':
              acc.push(`${item}`);
              break;
          }
          return acc;
        }, []);
      }
      switch (typeof jsonData) {
        case 'string':
          return [jsonData];
        case 'number':
        case 'boolean':
          return [`${jsonData}`];
        default:
          return [];
      }
    } catch {
      return [];
    }
  }
  return (
    value?.split(',')?.reduce((acc: Array<string>, v: string) => {
      const trimmedValue = (decode ? safelyDecodeURIComponent(v) : v).trim();
      if (_isNotBlank(trimmedValue)) {
        acc.push(trimmedValue);
      }
      return acc;
    }, []) ?? []
  );
};

interface MultiSelectV16Props {
  options: Array<{ value: string; label: string }>;
  defaultValue: Array<{ value: string; label: string }>;
  optionsLookupId: string | null;
  td: HTMLTableCellElement;
  isMultiSelect: boolean;
  onChange: (value: Array<{ value: string }> | { value: string } | null) => void;
}

const MultiSelectV16 = ({
  options,
  defaultValue,
  optionsLookupId,
  td,
  isMultiSelect,
  onChange,
}: MultiSelectV16Props) => {
  const tdRect = td.getBoundingClientRect();
  const { newAbortController } = useAbortController();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- SyncSelect and AsyncPaginate have incompatible ref types
  const selectRef = useRef<any>(null);

  useEffect(() => {
    (selectRef.current as { focus?: () => void } | null)?.focus?.();
  }, [selectRef]);

  const loadOptions = async (
    query: string,
    _loadedOptions: unknown,
    { page, perPage }: { page: number; perPage: number }
  ) => {
    try {
      const url = api('metadata.options.show', { id: optionsLookupId });
      const queryParams = new URLSearchParams({ page: String(page), perPage: String(perPage) });

      const trimmedQuery = (query ?? '').trim();
      if (_isNotEmpty(trimmedQuery)) {
        queryParams.set('query', trimmedQuery);
      }

      const {
        data: {
          data,
          links: { next },
        },
      } = await Http.get(`${url}?${queryParams.toString()}`, {
        signal: newAbortController().signal,
      });

      const hasMore = _notNil(next);

      const loadedOptions = data.map(({ value }: { value: string }) => ({
        value: isMultiSelect === true ? encodeURIComponent(value) : value,
        label: value,
      }));
      let nextPage = page;
      let nextPerPage = perPage;
      if (hasMore) {
        const nextUrl = new URL(next);
        nextPage = Number(nextUrl.searchParams.get('page'));
        nextPerPage = Number(nextUrl.searchParams.get('perPage'));
      }

      return {
        options: loadedOptions,
        hasMore,
        additional: { page: nextPage, perPage: nextPerPage },
      };
    } catch (error) {
      if (notAborted(error)) {
        ExceptionHandler.captureException(
          new InVivoError('Could not load metadata options', { cause: error, slug: 'multi-select-options-load' })
        );
      }
    }
    return { options: [], hasMore: false, additional: { page, perPage } };
  };

  return (
    <div
      style={{ width: `${tdRect.width}px`, height: `${tdRect.height}px` }}
      data-ismulti={isMultiSelect}
      data-test-component="HotMultiselect"
      data-test-element="container"
    >
      {_notNil(optionsLookupId) ? (
        <AsyncPaginate
          selectRef={selectRef}
          classNamePrefix="HoT-Multiselect"
          defaultValue={defaultValue}
          additional={{ page: 1, perPage: 10 }}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          loadOptions={loadOptions as any}
          isMulti={isMultiSelect}
          closeMenuOnSelect={false}
          autoFocus={true}
          defaultOptions
          menuIsOpen={true}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onChange={onChange as any}
          debounceTimeout={300}
        />
      ) : (
        <SyncSelect
          ref={selectRef}
          classNamePrefix="HoT-Multiselect"
          value={defaultValue}
          options={options}
          isMulti={isMultiSelect}
          menuIsOpen={true}
          autoFocus={true}
          closeMenuOnSelect={false}
          onChange={(args) => onChange(args as Array<{ value: string }> | { value: string } | null)}
        />
      )}
    </div>
  );
};

// Match V12 behavior: always decode URI component
const valueToChipV16 = (value: string, _isMultiSelect: boolean = false, isValid: boolean = true) => {
  const chip = document.createElement('span');
  chip.classList.add(styles.ValueChip);
  if (isValid === false) {
    chip.classList.add(styles.InvalidChip);
  }
  chip.innerText = safelyDecodeURIComponent(value);
  return chip;
};

/**
 * Add single click handler to cell dropdown arrow
 */
const addDropdownIconMousedownHandlerV16 = (
  hotInstance: Handsontable.Core & { multiSelectDropdownArrowListenerV16?: (event: Event) => void },
  _td: HTMLTableCellElement,
  _row: number,
  _column: number
) => {
  if (_isNil(hotInstance.multiSelectDropdownArrowListenerV16)) {
    hotInstance.multiSelectDropdownArrowListenerV16 = (event) => {
      const target = event.target as HTMLElement;
      if (target.classList.contains('multiSelectDropdownArrowV16')) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        // Get the row and column from data attributes
        const rowStr = target.getAttribute('data-row');
        const colStr = target.getAttribute('data-col');

        if (rowStr !== null && colStr !== null) {
          const row = parseInt(rowStr, 10);
          const col = parseInt(colStr, 10);

          // Use setTimeout to ensure this happens after current event processing
          setTimeout(() => {
            // Open the editor by selecting the cell
            hotInstance.selectCell(row, col);
            // Then explicitly open the editor
            const editor = hotInstance.getActiveEditor();
            if (_notNil(editor) && (editor.isOpened() ?? false) === false) {
              editor.beginEditing();
            }
          }, 0);
        }
      }
    };
    hotInstance.rootElement.addEventListener('mousedown', hotInstance.multiSelectDropdownArrowListenerV16, true);
    hotInstance.addHookOnce('afterDestroy', () => {
      if (_notNil(hotInstance.multiSelectDropdownArrowListenerV16)) {
        hotInstance.rootElement.removeEventListener('mousedown', hotInstance.multiSelectDropdownArrowListenerV16, true);
      }
    });
  }
};

/**
 * Validator for multi-select editor v16
 */
export const multiSelectInVivoValidatorV16: InVivoValidator = async (value, columnSettings) => {
  const required = columnSettings.required === true;
  const isMultiSelect = (columnSettings as { isMultiSelect?: boolean })?.isMultiSelect ?? true;
  const optionsLookupId = columnSettings?.optionsLookupId ?? null;
  const isLookup = _notNil(columnSettings?.optionsLookupId);

  const selectedOptionsSet = new Set<string>(
    isMultiSelect
      ? loadMultiSelectRawDataV16(value, columnSettings?.jsonEncoded ?? false, isLookup)
      : [(value ?? '').trim()].filter(_isNotBlank)
  );

  if (selectedOptionsSet.size === 0) {
    if (required) {
      return { valid: false, message: 'This field is required' };
    }
    return { valid: true };
  }
  if (isLookup) {
    return validationCache.validate(optionsLookupId, selectedOptionsSet);
  } else if (Array.isArray(columnSettings?.options)) {
    const optionsSet = new Set<string>(
      columnSettings.options.reduce<Array<string>>(
        (acc, item) => (_isNotBlank(item as string) ? [...acc, (item as string).trim()] : acc),
        []
      )
    );
    if (!isSubsetOfSets(selectedOptionsSet, optionsSet)) {
      return { valid: false, message: 'Selected options are not valid' };
    }
  }

  return { valid: true };
};

interface MultiSelectCellPropertiesV16 extends CellProperties {
  isMultiSelect?: boolean;
  jsonEncoded?: boolean;
  optionsLookupId?: string | null;
  options?: Array<string>;
  optionsComparator?: <T extends { label: string }>(a: T, b: T) => number;
}

/**
 * Custom renderer for multi-select cells v16
 */
export const multiSelectRendererV16 = (
  hotInstance: Handsontable.Core,
  td: HTMLTableCellElement,
  row: number,
  column: number,
  _prop: string | number,
  value: unknown,
  cellProperties: CellProperties
) => {
  const isError = td.getAttribute('data-type') === 'error';
  const isValid = hotInstance.getCellMeta(row, column)?.valid ?? true;
  td.innerHTML = '';

  if (isError || !isValid) {
    td.classList.add('htInvalid');
  }

  const cellProps = cellProperties as MultiSelectCellPropertiesV16;
  const isMultiSelect = cellProps?.isMultiSelect ?? true;
  const jsonEncoded = cellProps?.jsonEncoded ?? false;
  const optionsLookupId = cellProps?.optionsLookupId ?? null;
  const isLookup = _notNil(optionsLookupId);
  const isReadOnly = cellProps?.readOnly === true;

  if (isReadOnly) {
    td.style.padding = '0';
  }

  const flexContainer = document.createElement('div');
  flexContainer.classList.add(styles.FlexContainer);
  if (isReadOnly) {
    flexContainer.classList.add(styles.readOnly);
  }

  const valuesContainer = document.createElement('div');
  valuesContainer.classList.add(styles.ValuesContainer);

  if (_isNotBlank(value as string)) {
    const validOptions = new Set<string>(
      Array.isArray(cellProps?.options)
        ? cellProps.options.reduce<Array<string>>((acc, item) => (_isNotBlank(item) ? [...acc, item.trim()] : acc), [])
        : []
    );
    if (isMultiSelect === true) {
      // Match V12 behavior: do NOT decode here. valueToChipV16 decodes for display,
      // and decoding here would erode URL-encoding when values are written back on save.
      loadMultiSelectRawDataV16(value as string, jsonEncoded).forEach((v) => {
        const chipValue = v.trim();
        if (_isNotEmpty(chipValue)) {
          valuesContainer.appendChild(
            valueToChipV16(chipValue, true, isLookup ? isValid : validOptions.has(chipValue))
          );
        }
      });
    } else {
      // For single select lookup, values are NOT URL-encoded, so use as-is
      const trimmedValue = (value as string).trim();
      valuesContainer.appendChild(
        valueToChipV16(trimmedValue, false, isLookup ? isValid : validOptions.has(trimmedValue))
      );
    }
  }

  flexContainer.appendChild(valuesContainer);

  if (!isReadOnly) {
    const dropdownIconContainer = document.createElement('div');
    dropdownIconContainer.classList.add(styles.DropdownIcon, 'multiSelectDropdownArrowV16');
    dropdownIconContainer.style.cursor = 'pointer';
    dropdownIconContainer.innerText = '▼';
    dropdownIconContainer.setAttribute('data-row', String(row));
    dropdownIconContainer.setAttribute('data-col', String(column));
    flexContainer.appendChild(dropdownIconContainer);
  }

  td.appendChild(flexContainer);

  addDropdownIconMousedownHandlerV16(hotInstance, td, row, column);

  return td;
};

export class MultiSelectEditorV16 extends TextEditor {
  static type = 'multi-select-v16';

  private multiSelectContainer?: HTMLDivElement;
  private multiSelectOverlay?: HTMLDivElement;
  private root?: ReactDOM.Root;
  private displayed = false;
  private _initialValue?: unknown;
  private _originalValue?: unknown; // Store the true original value from prepare()
  private _renderKey = 0;
  private _isDestroyed = false;

  /**
   * Register the cell type with Handsontable v16
   */
  static register(type = MultiSelectEditorV16.type) {
    registerCellType(type, {
      editor: MultiSelectEditorV16 as unknown as typeof BaseEditor,
      renderer: multiSelectRendererV16,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      validator: fromInVivoValidator(multiSelectInVivoValidatorV16) as any,
    });
  }

  /**
   * Factory for creating validators with options
   */
  static validatorFactory(options: Array<string>, isMultiSelect: boolean, min = 0, max = Infinity) {
    const trimmedOptions = options.map((o) => o.trim());
    return (value: unknown, callback: (valid: boolean) => void) => {
      const selectedOptions =
        isMultiSelect === true
          ? String(value ?? '')
              .split(',')
              .map((o) => o.trim())
              .filter(_isNotEmpty)
          : [String(value ?? '').trim()].filter(_isNotEmpty);

      if (
        selectedOptions.length >= min &&
        selectedOptions.length <= max &&
        !selectedOptions.some((option) => !trimmedOptions.includes(option))
      ) {
        callback(true);
      } else {
        callback(false);
      }
    };
  }

  /**
   * Factory for creating lookup validators
   */
  static lookupValidatorFactory(isMultiSelect: boolean, min = 0, max = Infinity) {
    return (value: unknown, callback: (valid: boolean) => void) => {
      const selectedOptions =
        isMultiSelect === true
          ? String(value ?? '')
              .split(',')
              .filter(_isNotEmpty)
          : [String(value ?? '')].filter(_isNotEmpty);
      if (selectedOptions.length >= min && selectedOptions.length <= max) {
        callback(true);
      } else {
        callback(false);
      }
    };
  }

  init() {
    super.init();
    this.hot.addHook('afterDestroy', () => this.destroyElements());
  }

  /**
   * Override prepare to avoid v16 internal API issues
   * Do NOT call super.prepare() as it may call methods that don't exist in v16
   */
  prepare(
    row: number,
    col: number,
    prop: string | number,
    td: HTMLTableCellElement,
    originalValue: unknown,
    cellProperties: CellProperties
  ) {
    this.row = row;
    this.col = col;
    this.prop = prop;
    this.TD = td;
    this.originalValue = originalValue;
    this.cellProperties = cellProperties;

    // Store both original value (for restore) and initial value (for rendering)
    // Always reset to ensure no value leakage between cells
    this._originalValue = originalValue;
    this._initialValue = originalValue; // Preserve null/undefined to match test expectations
    this._renderKey++;

    // Clear TEXTAREA to prevent value leakage
    if (this.TEXTAREA instanceof HTMLTextAreaElement) {
      this.TEXTAREA.value = String(originalValue ?? '');
    }
  }

  createElements() {
    super.createElements();

    if (this.TEXTAREA instanceof HTMLTextAreaElement) {
      this.TEXTAREA.style.fontSize = '1px';
      this.TEXTAREA.style.opacity = '0';
    }

    this.multiSelectContainer = this.hot.rootDocument.createElement('div');
    this.multiSelectContainer.classList.add('multi-select-container');
    this.multiSelectContainer.setAttribute('data-testid', 'hot-multi-select-container');
    this.multiSelectContainer.style.display = 'none';

    this.multiSelectOverlay = this.hot.rootDocument.createElement('div');
    this.multiSelectOverlay.classList.add('multi-select-overlay');
    this.multiSelectOverlay.setAttribute('data-testid', 'hot-multi-select-overlay');

    this.multiSelectContainer.appendChild(this.multiSelectOverlay);
    this.hot.rootDocument.body.appendChild(this.multiSelectContainer);

    const handleMouseDown = (event: MouseEvent) => {
      if (_notNil(this.multiSelectContainer) && event.target === this.multiSelectContainer) {
        this.finishEditing(false, false);
        const cellProps = this.cellProperties as { row?: number; col?: number };
        const { row, col } = cellProps;
        if (row !== undefined && col !== undefined) {
          this.hot.selectCell(row, col);
        }
      } else {
        event.stopImmediatePropagation();
      }
    };

    const handleClick = () => {
      const firstChild = this.multiSelectOverlay?.firstChild as HTMLElement | undefined;
      const isMultiSelect = firstChild?.getAttribute('data-ismulti') === 'true';
      if (!isMultiSelect) {
        const cellProps = this.cellProperties as { row?: number; col?: number };
        const { row, col } = cellProps;
        if (row !== undefined && col !== undefined) {
          this.hot.selectCell(row, col);
        }
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const firstChild = this.multiSelectOverlay?.firstChild as HTMLElement | undefined;
      const isMultiSelect = firstChild?.getAttribute('data-ismulti') === 'true';
      const cellProps = this.cellProperties as { row?: number; col?: number };
      const { row, col } = cellProps;
      if (isMultiSelect) {
        if (e.code === 'Escape') {
          if (row !== undefined && col !== undefined) {
            this.hot.selectCell(row, col);
          }
        }
      } else {
        switch (e.code) {
          case 'Enter':
          case 'Escape':
            if (row !== undefined && col !== undefined) {
              this.hot.selectCell(row, col);
            }
            break;
        }
      }
      e.stopPropagation();
    };

    this.multiSelectContainer.addEventListener('mousedown', handleMouseDown, { passive: false });
    this.multiSelectContainer.addEventListener('click', handleClick, { passive: true });
    this.multiSelectContainer.addEventListener('keydown', handleKeyDown);

    this.hot.addHookOnce('afterDestroy', () => {
      if (_notNil(this.multiSelectContainer)) {
        this.multiSelectContainer.removeEventListener('mousedown', handleMouseDown);
        this.multiSelectContainer.removeEventListener('click', handleClick);
        this.multiSelectContainer.removeEventListener('keydown', handleKeyDown);
      }
    });
  }

  render() {
    if (
      this.isOpened() &&
      _notNil(this.multiSelectOverlay) &&
      _notNil(this.cellProperties) &&
      _notNil(this.multiSelectContainer) &&
      this.multiSelectContainer.style.display === 'block'
    ) {
      const cellProps = this.cellProperties as MultiSelectCellPropertiesV16 & {
        optionsMap?: Map<string, { value: string; label: string }>;
      };
      const isMultiSelect = cellProps?.isMultiSelect ?? true;
      const optionsLookupId = cellProps?.optionsLookupId ?? null;
      const isLookup = _notNil(optionsLookupId);
      const jsonEncoded = cellProps?.jsonEncoded ?? false;
      const optionsComparator = cellProps?.optionsComparator;
      // Match V12 behavior for optionsMap
      const optionsMap: Map<string, { value: string; label: string }> =
        cellProps?.optionsMap ??
        new Map(
          (cellProps?.options ?? []).reduce(
            (
              acc: Array<[string, { value: string; label: string }]>,
              option: string | { value: string; label: string }
            ) => {
              if (typeof option === 'string' && _isNotBlank(option)) {
                const value = option.trim();
                // Match V12: decode for multi-select, plain value for single select
                acc.push([value, { value, label: isMultiSelect === true ? safelyDecodeURIComponent(value) : value }]);
              } else if (
                typeof option === 'object' &&
                Object.prototype.hasOwnProperty.call(option, 'value') &&
                _isNotBlank(option.value) &&
                Object.prototype.hasOwnProperty.call(option, 'label') &&
                _isNotEmpty(option.label)
              ) {
                // Match V12: use option as-is for object options
                acc.push([option.value, option]);
              }
              return acc;
            },
            []
          )
        );
      // Use _initialValue instead of getValue() which reads from TEXTAREA that may be empty
      // If _initialValue is undefined, use empty string to prevent value leakage
      const rawValue = _notNil(this._initialValue) ? String(this._initialValue) : '';

      const defaultValue = (
        isMultiSelect === true ? loadMultiSelectRawDataV16(rawValue, jsonEncoded) : [rawValue ?? '']
      ).reduce((acc: Array<{ value: string; label: string }>, optionValue: string) => {
        if (_isNotBlank(optionValue)) {
          if (isLookup) {
            // For lookup fields, values may be URL-encoded (multi-select) or plain (single select)
            acc.push({
              label: isMultiSelect === true ? safelyDecodeURIComponent(optionValue) : optionValue,
              value: optionValue,
            });
          } else {
            // For non-lookup fields, get option from optionsMap
            const option = optionsMap.get(optionValue.trim());
            if (_notNil(option)) {
              acc.push(option);
            }
          }
        }
        return acc;
      }, []);

      const handleOnChange = (value: Array<{ value: string }> | { value: string } | null) => {
        if (isMultiSelect === true && Array.isArray(value)) {
          const updatedData = value.reduce(
            (acc: Array<string>, item: { value: string }) => (_isNotBlank(item?.value) ? [...acc, item.value] : acc),
            [] as Array<string>
          );

          // Match V12 behavior: values are already URL-encoded from loadOptions,
          // so just join with comma or JSON.stringify based on jsonEncoded flag
          this.setValue(jsonEncoded === true ? JSON.stringify(updatedData) : updatedData.join(','));
        } else {
          const selectedValue = (value as { value: string } | null)?.value ?? null;
          this.setValue(selectedValue);
          this.finishEditing();
        }
      };

      if (_isNil(this.root) && _notNil(this.multiSelectOverlay)) {
        this.root = ReactDOM.createRoot(this.multiSelectOverlay);
      }

      this.root?.render(
        <StrictMode>
          <MultiSelectV16
            key={this._renderKey}
            options={Array.from(optionsMap.values()).sort(
              optionsComparator ??
                ((left, right) => left.label.localeCompare(right.label, undefined, { numeric: true }))
            )}
            defaultValue={defaultValue}
            optionsLookupId={optionsLookupId}
            td={this.TD}
            isMultiSelect={isMultiSelect}
            onChange={handleOnChange}
          />
        </StrictMode>
      );
    }
  }

  destroyElements() {
    this._isDestroyed = true;

    // Capture references before clearing
    const rootToUnmount = this.root;
    const containerToRemove = this.multiSelectContainer;

    // Clear references immediately to prevent double-cleanup
    this.root = undefined;
    this.multiSelectContainer = undefined;

    // Defer React unmount to avoid "unmount during render" race condition
    if (_notNil(rootToUnmount)) {
      queueMicrotask(() => {
        try {
          rootToUnmount.unmount();
        } catch {
          // Ignore errors if already unmounted
        }
      });
    }

    // Clean up DOM
    if (_notNil(containerToRemove)) {
      containerToRemove.innerHTML = '';
      containerToRemove.remove();
    }
  }

  finishEditing(restoreOriginalValue = false, ctrlDown = false) {
    // If instance is destroyed, don't call any Handsontable methods
    if (this._isDestroyed) {
      return;
    }

    // Save the value BEFORE closing (which clears _initialValue)
    let valueToSave: unknown = undefined;
    if (restoreOriginalValue) {
      // Use _originalValue for restore (which never changes after prepare)
      valueToSave = this._originalValue ?? this.originalValue;
    } else {
      // Save the new value - use _initialValue (updated during editing) not getValue() (TEXTAREA may be wrong)
      // Check for undefined explicitly - null IS a valid value (means "cleared")
      valueToSave = this._initialValue !== undefined ? this._initialValue : (this._originalValue ?? this.getValue());
    }
    // Close first to clean up, but preserve the value
    this.close();
    if (restoreOriginalValue) {
      if (_notNil(valueToSave)) {
        this.hot.setDataAtCell(this.row, this.col, valueToSave);
      }
    } else {
      // Allow null to be saved (clears the cell), but skip if unchanged
      const hasValue = valueToSave !== undefined && (valueToSave === null || _isNotBlank(valueToSave as string));
      if (hasValue && valueToSave !== this._originalValue) {
        this.hot.setDataAtCell(this.row, this.col, valueToSave);
      }
    }

    super.finishEditing(restoreOriginalValue, ctrlDown);
  }

  setValue(newValue: unknown) {
    // Never let `undefined`/`null` reach the textarea: the DOM coerces them to the literal
    // strings "undefined"/"null" (e.g. base TextEditor.refreshValue() calls setValue() with an
    // empty cell's sourceData === undefined), which then get persisted and rejected by the API.
    // `_initialValue` keeps the original value so finishEditing()'s save logic (null = clear) is intact.
    super.setValue(_isNil(newValue) ? '' : (newValue as string));
    // Update _initialValue when value changes during editing
    // This must happen BEFORE render() to ensure the value is preserved
    this._initialValue = newValue;
    this.render();
  }

  getValue() {
    if (!(this.TEXTAREA instanceof HTMLTextAreaElement)) {
      return '';
    }
    const value = this.TEXTAREA.value;
    // Defense-in-depth: a textarea populated with a coerced nil (legacy/edge paths) must not
    // leak the literal "undefined"/"null" string into the saved cell value.
    return value === 'undefined' || value === 'null' ? '' : (value ?? '');
  }

  open() {
    // Create elements if not yet created
    if (_notNil(this.multiSelectContainer) === false) {
      this.createElements();
    }
    // Don't call super.open() to avoid v16 internal issues
    this.display();
  }

  close() {
    if (!this.displayed) {
      return;
    }
    this.display(false);
    // Clear _initialValue to prevent value leakage between cells
    this._initialValue = undefined;
    // Don't call super.close() to avoid v16 internal issues
    if (!this._isDestroyed) {
      this.hot.render();
      requestAnimationFrame(() => {
        if (!this._isDestroyed) {
          this.hot.refreshDimensions();
        }
      });
    }
  }

  /**
   * Begin editing
   */
  beginEditing(_initialValue?: string, _event?: Event) {
    this.open();
  }

  isOpened() {
    return this.displayed;
  }

  display(display = true) {
    this.displayed = display;
    if (display === true) {
      const offset = this.TD.getBoundingClientRect();
      if (_notNil(this.multiSelectOverlay)) {
        this.multiSelectOverlay.style.top = `${offset.top}px`;
        this.multiSelectOverlay.style.left = `${offset.left}px`;
      }
      if (this.TEXTAREA instanceof HTMLTextAreaElement) {
        this.TEXTAREA.style.width = `${offset.width}px`;
        this.TEXTAREA.style.height = `${offset.height}px`;
      }
      if (_notNil(this.multiSelectContainer)) {
        this.multiSelectContainer.style.display = 'block';
      }
      this.render();
    } else {
      if (_notNil(this.multiSelectContainer)) {
        this.multiSelectContainer.style.display = 'none';
      }
      // Don't unmount the React root - just hide the container
      // This allows reusing the same root on next open
    }
  }

  focus() {
    // No-op for multi-select editor
  }
}
